<script lang="ts">
	import { Button, ButtonSize, ButtonType } from '@repo/commons/components'

	export type PageChangeDetail = { page: number; pageSize: number }

	interface Props {
		/** Current page (0-based). */
		currentPage: number
		/** Total number of pages. */
		totalPages: number
		/** Total item count across all pages. */
		totalCount: number
		/** Items per page. */
		itemsPerPage: number
		/** Disable controls when loading. */
		loading?: boolean
		/** Called when user changes page. */
		onPageChange?: (detail: PageChangeDetail) => void
	}

	let {
		currentPage,
		totalPages,
		totalCount,
		itemsPerPage,
		loading = false,
		onPageChange
	}: Props = $props()

	const range = $derived.by(() => {
		if (totalCount === 0) return { start: 0, end: 0 }
		const start = currentPage * itemsPerPage + 1
		const end = Math.min((currentPage + 1) * itemsPerPage, totalCount)
		return { start, end }
	})

	function getPageNumbers(cur: number, total: number): (number | 'ellipsis')[] {
		if (total <= 1) return []
		if (total <= 7) return Array.from({ length: total }, (_, i) => i)
		const indices = new Set<number>(
			[0, total - 1, cur, cur - 1, cur + 1].filter((p) => p >= 0 && p < total)
		)
		const sorted = [...indices].sort((a, b) => a - b)
		const result: (number | 'ellipsis')[] = []
		for (let i = 0; i < sorted.length; i++) {
			if (i > 0 && sorted[i]! - sorted[i - 1]! > 1) result.push('ellipsis')
			result.push(sorted[i]!)
		}
		return result
	}

	const pageNumbers = $derived(getPageNumbers(currentPage, totalPages))

	function goToPage(page: number) {
		if (loading) return
		const next = Math.max(0, Math.min(page, totalPages - 1))
		if (next === currentPage) return
		onPageChange?.({ page: next, pageSize: itemsPerPage })
	}
</script>

<footer class="pagination-container">
	<nav class="pagination-nav" aria-label="Table pagination">
		{#if pageNumbers.length > 0}
			<Button
				buttonText="Previous"
				buttonType={ButtonType.TRANSPARENT}
				buttonSize={ButtonSize.SMALL}
				disabled={currentPage === 0 || loading}
				onclick={() => goToPage(currentPage - 1)}
				iconProps={{ name: 'arrowLeft', size: 'sm' }}
				thin={true}
			/>
		{/if}

		<div class="pagination-pages">
			{#each pageNumbers as entry}
				{#if entry === 'ellipsis'}
					<span class="pagination-ellipsis" aria-hidden="true">…</span>
				{:else}
					<Button
						buttonText={(entry + 1).toString()}
						buttonType={entry === currentPage
							? ButtonType.SECONDARY_OUTLINE
							: ButtonType.TRANSPARENT}
						buttonSize={ButtonSize.SMALL}
						disabled={loading}
						onclick={() => goToPage(entry)}
						thin={true}
					/>
				{/if}
			{/each}
		</div>

		{#if pageNumbers.length > 0}
			<Button
				buttonText="Next"
				buttonType={ButtonType.TRANSPARENT}
				buttonSize={ButtonSize.SMALL}
				disabled={currentPage >= totalPages - 1 || loading}
				onclick={() => goToPage(currentPage + 1)}
				iconPosition="right"
				iconProps={{ name: 'arrowRight', size: 'sm' }}
				thin={true}
			/>
		{/if}
	</nav>

	<p class="pagination-info" aria-live="polite">
		Showing {range.start} – {range.end} of {totalCount}
	</p>
</footer>

<style>
	.pagination-container {
		@apply flex flex-wrap items-center justify-between gap-4 py-6 bg-white;
	}

	.pagination-nav {
		@apply flex items-center gap-2 flex-wrap font-normal;
	}

	.pagination-pages {
		@apply flex items-center gap-1;
	}

	.pagination-ellipsis {
		@apply min-w-[24px] text-center text-sm text-[#001D33B2];
		padding: 0 4px;
	}

	.pagination-info {
		@apply text-sm text-[#001D33B2] font-normal;
	}
</style>
